<template lang="pug">
  .flipClock
    .flipClockSection
      flip-unit-container(
        unit="days"
        :digit="days"
        :shuffle="daysShuffle")
      .flipClockSectionName {{$t('broadcast.days')}}
    .flipClockSection
      flip-unit-container(
        unit="hours"
        :digit="hours"
        :shuffle="hoursShuffle")
      .flipClockSectionName {{$t('broadcast.hours')}}
    .flipClockSection
      flip-unit-container(
        unit="minutes"
        :digit="minutes"
        :shuffle="minutesShuffle")
      .flipClockSectionName {{$t('broadcast.minutes')}}
    .flipClockSection
      flip-unit-container(
        unit="seconds"
        :digit="seconds"
        :shuffle="secondsShuffle")
      .flipClockSectionName {{$t('broadcast.seconds')}}
</template>

<script>
  import FlipUnitContainer from "./FlipUnitContainer";
  export default {
    name: "Clocks",
    props: {
      initialTimestamp: {
        type: Number,
        required: true
      }
    },
    components: {
      FlipUnitContainer
    },
    data() {
      return {
        days: 0,
        daysShuffle: true,
        hours: 0,
        hoursShuffle: true,
        minutes: 0,
        minutesShuffle: true,
        seconds: 0,
        secondsShuffle: true,
        timerID: null
      }
    },
    mounted() {
      this.timerID = setInterval(
        () => {
          this.updateTime()
        },
        50
      );
    },

    beforeDestroy() {
      clearInterval(this.timerID);
    },
    methods: {
      updateTime() {
        const eventDate = new Date(this.initialTimestamp * 1000)

        // get new date
        const time = new Date;
        const diff = (eventDate - time) / 1000

        if (diff <= 0) {
          clearInterval(this.timerID)
          return
        }

        // set time units
        const days = Math.floor(diff / (60 * 60 * 24));
        // const days = 1;
        const hours = Math.floor(diff / (60 * 60)) - days * 24;
        const minutes = Math.floor(diff / 60) - (days * 24 * 60) - (hours * 60);
        const seconds = Math.floor(diff - (minutes * 60) - (hours * 60 * 60) - (days * 24 * 60 * 60));
        // on days change, update hours and shuffle
        if( days !== this.days) {
          const daysShuffle = !this.daysShuffle;
          this.days = days;
          this.daysShuffle = daysShuffle
        }
        // on hour change, update hours and shuffle
        if( hours !== this.hours) {
          const hoursShuffle = !this.hoursShuffle;
          this.hours = hours;
          this.hoursShuffle = hoursShuffle
        }
        // on minute change, update minutes and shuffle
        if( minutes !== this.minutes) {
          const minutesShuffle = !this.minutesShuffle;
          this.minutes = minutes
          this.minutesShuffle = minutesShuffle
        }
        // on second change, update seconds and shuffle
        if( seconds !== this.seconds) {
          const secondsShuffle = !this.secondsShuffle;
          this.seconds = seconds;
          this.secondsShuffle = secondsShuffle;
        }
      }
    }
  }
</script>

<style lang="sass">
  .flipClock
    $easeInOut: cubic-bezier(0.455, 0.03, 0.515, 0.955)

    $turnDuration: 0.6s

    // Animation mixin
    =animate($name, $easing, $duration: 300ms, $delay: 0s, $iteration: 1, $direction: normal, $fill-mode: forwards)
      animation: $name $duration $easing $delay $iteration $direction $fill-mode

    $border: whitesmoke
    $card: white
    $borderRadius: 5px

    display: flex
    justify-content: space-between

    .flipClockSection
      text-align: center
      width: 4.375rem
      &:not(:last-child)
        margin-right: 10px

    .flipUnitContainer
      height: 4.1875rem
      display: block
      position: relative
      perspective-origin: 50% 50%
      perspective: 300px
      background-color: $card
      border-radius: $borderRadius
      font-size: 4.3125rem
      &::before, &::after
        content: ''
        position: absolute
        top: calc(50% - 0.4375rem / 2)
        background-color: white
        width: 0.25rem
        height: 0.5625rem
        border-radius: 1px
        z-index: 1
      &::before
        left: -0.125rem
      &::after
        right: -0.125rem
    .upperCard, .lowerCard
      display: flex
      position: relative
      justify-content: center
      width: 100%
      height: 50%
      overflow: hidden
      border: 1px solid $border

      span
        color: white

    .upperCard
      background: #053c76
      align-items: flex-end
      border-bottom: 0.5px solid #053c76

      border-top-left-radius: $borderRadius
      border-top-right-radius: $borderRadius

      span
        transform: translateY(50%)

    .lowerCard
      background: #50769d
      align-items: flex-start
      border-top: 0.5px solid #053c76
      border-bottom-left-radius: $borderRadius
      border-bottom-right-radius: $borderRadius

      span
        transform: translateY(-50%)

    .flipCard
      display: flex
      justify-content: center
      position: absolute
      left: 0
      width: 100%
      height: 50%
      overflow: hidden
      backface-visibility: hidden

      span
        color: white

      &.unfold
        top: 50%
        align-items: flex-start
        transform-origin: 50% 0%
        transform: rotateX(180deg) // from 180 to 0
        background-color: #50769d
        border-bottom-left-radius: $borderRadius
        border-bottom-right-radius: $borderRadius
        border: 0.5px solid $border
        border-top: 0.5px solid #053c76

        span
          transform: translateY(-50%)

      &.fold
        top: 0%
        align-items: flex-end
        transform-origin: 50% 100%
        transform: rotateX(0deg) // from 0 to -180
        background: #053c76
        border-top-left-radius: $borderRadius
        border-top-right-radius: $borderRadius
        border: 0.5px solid $border
        border-bottom: 0.5px solid #053c76

        span
          transform: translateY(50%)

    .fold
      @include animate(fold, $easeInOut, 0.6s)
      transform-style: preserve-3d

    .unfold
      @include animate(unfold, $easeInOut, 0.6s)
      transform-style: preserve-3d

    @keyframes fold
      0%
        transform: rotateX(0deg)
      100%
        transform: rotateX(-180deg)

    @keyframes unfold
      0%
        transform: rotateX(180deg)
      100%
        transform: rotateX(0deg)
    .flipClockSectionName
      margin: .7rem 0
</style>
